/**
 * Turns an API error into one readable line (#19): the validator's field messages, the API's
 * `error` string (409 duplicate year, 404), else the interceptor's message. The interceptor in
 * services/api.ts only forwards `data.message`, which the API never sends, so the body is read here.
 * ContractDetail uses this copy since #24 (its own copy was the follow-up named here).
 */
export function describeApiError(err: unknown): string {
  const fallback = 'Something went wrong';
  if (!err || typeof err !== 'object') return fallback;

  const { response, errorMessage, message } = err as {
    response?: { data?: unknown };
    errorMessage?: unknown;
    message?: unknown;
  };
  const data = response?.data;

  if (data && typeof data === 'object') {
    const { errors, error, title } = data as { errors?: unknown; error?: unknown; title?: unknown };

    if (errors && typeof errors === 'object') {
      const messages = Object.values(errors as Record<string, unknown>)
        .flatMap((value) => (Array.isArray(value) ? value : [value]))
        .filter((value): value is string => typeof value === 'string' && value.length > 0);
      if (messages.length > 0) return messages.join('; ');
    }
    if (typeof error === 'string' && error) return error;
    if (typeof title === 'string' && title) return title;
  }

  if (typeof errorMessage === 'string' && errorMessage) return errorMessage;
  if (typeof message === 'string' && message) return message;
  return fallback;
}

/** The `{ error, reason }` / `{ error, code, fieldErrors }` body of a refused extraction apply or reject */
export function apiErrorBody(err: unknown): { status?: number; reason?: string; code?: string; fieldErrors?: unknown[] } {
  if (!err || typeof err !== 'object') return {};
  const { response } = err as { response?: { status?: number; data?: unknown } };
  const data = (response?.data && typeof response.data === 'object' ? response.data : {}) as {
    reason?: unknown;
    code?: unknown;
    fieldErrors?: unknown;
  };
  return {
    status: response?.status,
    reason: typeof data.reason === 'string' ? data.reason : undefined,
    code: typeof data.code === 'string' ? data.code : undefined,
    fieldErrors: Array.isArray(data.fieldErrors) ? data.fieldErrors : undefined,
  };
}
